import type { AxiosInstance } from 'axios';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { ServiceResult } from '../types/common/ServiceResult';
import type { UserStatsModel } from '../types/admin';
import type { ListingStatsModel } from '../types/admin';
import { apiClient } from '../client/client';

export interface ListingStatsQuery {
  StartDate?: string | null;
  EndDate?: string | null;
  TopCategoryId?: number | null;
  SecondaryCategoryId?: number | null;
  ProductTypeId?: number | null;
  StatusId?: number | null;
  CityId?: number | null;
  IsDeleted?: boolean | null;
}

/**
 * Admin panel API – user stats, listing stats, users count, listings count.
 * Optional: deleteUser, sendActivationEmail (backend may expose these later).
 */
export class AdminPanelService {
  private static readonly BASE = '/api/admin-panel';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async getUsersCount(): Promise<ServiceResultT<number>> {
    const { data } = await this.client.get<ServiceResultT<number>>(
      `${AdminPanelService.BASE}/users-count`
    );
    return data;
  }

  async getListingsCount(statusId: number): Promise<ServiceResultT<number>> {
    const { data } = await this.client.get<ServiceResultT<number>>(
      `${AdminPanelService.BASE}/listings-count/${statusId}`
    );
    return data;
  }

  async getUserStats(): Promise<ServiceResultT<UserStatsModel>> {
    const { data } = await this.client.get<ServiceResultT<UserStatsModel>>(
      `${AdminPanelService.BASE}/user-stats`
    );
    return data;
  }

  async getListingStats(
    params?: ListingStatsQuery
  ): Promise<ServiceResultT<ListingStatsModel>> {
    const { data } = await this.client.get<ServiceResultT<ListingStatsModel>>(
      `${AdminPanelService.BASE}/listing-stats`,
      { params: params ?? {} }
    );
    return data;
  }

  /**
   * Delete user. Backend: DELETE /api/user/{userId} if available.
   */
  async deleteUser(userId: string): Promise<ServiceResult> {
    const { data } = await this.client.delete<ServiceResult>(
      `/api/user/${userId}`
    );
    return data;
  }

  /**
   * Manually send email activation code to user. Backend: e.g. POST /api/admin-panel/send-activation-email
   */
  async sendActivationEmail(userId: string): Promise<ServiceResult> {
    const { data } = await this.client.post<ServiceResult>(
      `${AdminPanelService.BASE}/send-activation-email`,
      { userId }
    );
    return data;
  }
}

export const adminPanelService = new AdminPanelService();
